import React, { memo, useState } from 'react'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { PrimeIcons } from 'primereact/api'

import { dateLocalize } from '../date-localize'
import { ApiCustomer } from '../../common/redux/api/web-api'
import { Table } from '../../common/uikit/Table'
import { Input } from '../../common/uikit/Input'
import useText from '../../common/hooks/useText'

export type SupportApiCustomersWidgetProps = {
    apiCustomers: ApiCustomer[]
    openApiCustomer: (ac: ApiCustomer | undefined) => void
    deleteCustomer: (ac: ApiCustomer) => void
    isLoading?: boolean
}

export const SupportApiCustomersWidget = memo(function SupportApiCustomersWidget({
    apiCustomers,
    openApiCustomer,
    deleteCustomer,
    isLoading,
}: SupportApiCustomersWidgetProps): React.ReactElement {
    const text = useText()
    const [globalFilter, setGlobalFilter] = useState('')

    return (
        <Card>
            <div className="mb-3 mt-2 flex gap-3 justify-content-end">
                <Button label={text('btnCreate')} onClick={() => openApiCustomer(undefined)}></Button>
                <Input
                    id="eapi-search-input"
                    type="search"
                    className="w-3"
                    data-testid="search"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={text('msgSearch')}
                    icon={PrimeIcons.SEARCH}
                />
            </div>
            <Table
                value={apiCustomers}
                globalFilter={globalFilter}
                globalFilterFields={['id', 'name', 'email', 'validTo', 'validFrom']}
                dataKey="id"
                sortField="id"
                paginator
                sortOrder={+1}
                loading={isLoading}
                rows={20}
                emptyMessage={text('msgNoAvailableData')}
            >
                <Column field="id" header={text('lblId')} sortable style={{ width: '3rem' }}></Column>
                <Column
                    field="status"
                    header={text('lblStatus')}
                    body={(apiCustomer: ApiCustomer) => (
                        <span>
                            {apiCustomer.enabled ? (
                                <i className="pi pi-circle-fill text-green-500"></i>
                            ) : (
                                <i className="pi pi-circle-fill text-red-300"></i>
                            )}
                        </span>
                    )}
                ></Column>
                <Column
                    field="name"
                    header={text('lblName')}
                    sortable
                    style={{ width: '10rem', wordBreak: 'break-word' }}
                ></Column>

                <Column
                    field="validFrom"
                    header={text('lblValidFrom')}
                    body={(apiCustomer: ApiCustomer) =>
                        new Date(dateLocalize(apiCustomer.validFrom)).toLocaleDateString('sv-SE')
                    }
                    sortable
                ></Column>
                <Column
                    field="validTo"
                    header={text('lblValidTo')}
                    body={(apiCustomer: ApiCustomer) =>
                        new Date(dateLocalize(apiCustomer.validTo)).toLocaleDateString('sv-SE')
                    }
                    sortable
                ></Column>

                <Column
                    field="email"
                    header={text('lblEmail')}
                    sortable
                    style={{ width: '20rem', wordBreak: 'break-word' }}
                ></Column>

                <Column
                    field="callBack"
                    header={text('lblCallback')}
                    body={(apiCustomer: ApiCustomer) => (
                        <span>
                            {apiCustomer.callbackEnabled ? (
                                <i className="pi pi-check-square text-green-500" title="true"></i>
                            ) : (
                                <i className="pi pi-minus-circle text-red-300" title="false"></i>
                            )}
                        </span>
                    )}
                ></Column>
                <Column
                    body={(apiCustomer: ApiCustomer) => (
                        <span className="flex gap-3">
                            <Button
                                size="small"
                                rounded
                                severity="info"
                                outlined
                                icon="pi pi-pencil"
                                data-testid="edit-icon"
                                title={text('btnEdit')}
                                onClick={() => openApiCustomer(apiCustomer)}
                            ></Button>

                            <Button
                                data-testid="delete-icon"
                                size="small"
                                icon="pi pi-trash"
                                rounded
                                outlined
                                title={text('btnDelete')}
                                severity="danger"
                                onClick={() => deleteCustomer(apiCustomer)}
                            />
                        </span>
                    )}
                ></Column>
            </Table>
        </Card>
    )
})
